const ALLOWED_META_KEYS = new Set([
  'name',
  'message',
  'code',
  'status',
  'statusText',
  'method',
])

const REDACTED = '[Redacted]'

type AxiosErrorLike = {
  isAxiosError: true
  name?: string
  message?: string
  code?: string
  config?: { method?: string; url?: string }
  response?: { status?: number; statusText?: string }
}

const isPrimitive = (value: unknown): boolean =>
  value === null ||
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean'

const isAxiosErrorLike = (value: unknown): value is AxiosErrorLike =>
  typeof value === 'object' &&
  value !== null &&
  (value as { isAxiosError?: boolean }).isAxiosError === true

export const stripQuery = (url?: string): string | undefined => {
  if (!url) {
    return undefined
  }
  const hashIndex = url.indexOf('#')
  const withoutHash = hashIndex === -1 ? url : url.slice(0, hashIndex)
  const queryIndex = withoutHash.indexOf('?')
  return queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex)
}

const sanitizeAxiosError = (
  error: AxiosErrorLike,
): Record<string, unknown> => ({
  name: error.name,
  message: error.message,
  code: error.code,
  method: error.config?.method,
  url: stripQuery(error.config?.url),
  status: error.response?.status,
  statusText: error.response?.statusText,
})

const sanitizePlainObject = (value: Record<string, unknown>): unknown => {
  const result: Record<string, unknown> = {}
  let kept = 0
  for (const [key, entry] of Object.entries(value)) {
    if (ALLOWED_META_KEYS.has(key) && isPrimitive(entry)) {
      result[key] = entry
      kept += 1
    }
  }
  return kept > 0 ? result : REDACTED
}

export const sanitizeLogValue = (value: unknown): unknown => {
  if (value === undefined || value === null) {
    return value
  }
  if (isPrimitive(value)) {
    return value
  }
  if (isAxiosErrorLike(value)) {
    return sanitizeAxiosError(value)
  }
  if (value instanceof Error) {
    return { name: value.name, message: value.message }
  }
  if (typeof value === 'object') {
    return sanitizePlainObject(value as Record<string, unknown>)
  }
  return REDACTED
}

export const logger = {
  error: (message: string, meta?: unknown): void => {
    if (meta === undefined) {
      console.error(message)
      return
    }
    console.error(message, sanitizeLogValue(meta))
  },
  info: (message: string, meta?: unknown): void => {
    if (meta === undefined) {
      console.info(message)
      return
    }
    console.info(message, sanitizeLogValue(meta))
  },
  warn: (message: string, meta?: unknown): void => {
    if (meta === undefined) {
      console.warn(message)
      return
    }
    console.warn(message, sanitizeLogValue(meta))
  },
}
